/** @graphql cc **/
import { useMutation } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import { useRef } from 'react';

import type { CommentsDataState } from '@confluence/comments-data';
import type {
	CommentInlineLocation,
	ActiveCommentInlineResolveProperties,
} from '@confluence/comments-panel-queries';

import type {
	MarkDanglingCommentsMutation as MarkDanglingCommentsMutationType,
	MarkDanglingCommentsMutationVariables,
} from './__types__/MarkDanglingCommentsMutation';
import type {
	UnmarkDanglingCommentsMutation as UnmarkDanglingCommentsMutationType,
	UnmarkDanglingCommentsMutationVariables,
} from './__types__/UnmarkDanglingCommentsMutation';

// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- Read https://go/connie-relay-migration-fyi
const MarkDanglingCommentsMutation = gql`
	mutation MarkDanglingCommentsMutation(
		$input: ConfluenceMarkCommentAsDanglingInput!
		$cloudId: ID!
	) {
		confluence_markCommentAsDangling(input: $input, cloudId: $cloudId) {
			success
			errors {
				message
			}
		}
	}
`;

// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- Read https://go/connie-relay-migration-fyi
const UnmarkDanglingCommentsMutation = gql`
	mutation UnmarkDanglingCommentsMutation(
		$input: ConfluenceUnmarkCommentAsDanglingInput!
		$cloudId: ID!
	) {
		confluence_unmarkCommentAsDangling(input: $input, cloudId: $cloudId) {
			success
			errors {
				message
			}
		}
	}
`;

type UseDanglingCommentsProps = {
	updatedCommentsDataMap: CommentsDataState['commentsDataMap'];
	setCommentsDataMap: (commentsDataMap: CommentsDataState['commentsDataMap']) => void;
};

export const useDanglingComments = ({
	updatedCommentsDataMap,
	setCommentsDataMap,
}: UseDanglingCommentsProps) => {
	const commentDanglingRef = useRef<string | null>(null);

	const [markCommentAsDangling] = useMutation<
		MarkDanglingCommentsMutationType,
		MarkDanglingCommentsMutationVariables
	>(MarkDanglingCommentsMutation, {
		onCompleted: (data: MarkDanglingCommentsMutationType) => {
			if (data?.confluence_markCommentAsDangling?.success) {
				// we have to set isDangling for the comment thread in the commentsDataMap
				const threadKey = commentDanglingRef.current;
				if (!threadKey) {
					return;
				}

				setCommentsDataMap({
					...updatedCommentsDataMap,
					inline: {
						...updatedCommentsDataMap.inline,
						[threadKey]: {
							...updatedCommentsDataMap.inline[threadKey],
							location: {
								...updatedCommentsDataMap.inline[threadKey].location,
								inlineResolveProperties: {
									...(updatedCommentsDataMap.inline[threadKey].location as CommentInlineLocation)
										.inlineResolveProperties,
									isDangling: true,
								} as ActiveCommentInlineResolveProperties,
							},
						},
					},
				});

				commentDanglingRef.current = null;
			}
		},
		onError: () => {
			// TODO: handle error, for now it's okay if not since BE will eventually handle the dangling comment
			// we could retry this mutation if we want to
		},
	});

	const [unmarkCommentAsDangling] = useMutation<
		UnmarkDanglingCommentsMutationType,
		UnmarkDanglingCommentsMutationVariables
	>(UnmarkDanglingCommentsMutation, {
		onCompleted: (data: UnmarkDanglingCommentsMutationType) => {
			if (data?.confluence_unmarkCommentAsDangling?.success) {
				const threadKey = commentDanglingRef.current;

				if (!threadKey) {
					return;
				}

				setCommentsDataMap({
					...updatedCommentsDataMap,
					inline: {
						...updatedCommentsDataMap.inline,
						[threadKey]: {
							...updatedCommentsDataMap.inline[threadKey],
							location: {
								...updatedCommentsDataMap.inline[threadKey].location,
								inlineResolveProperties: {
									...(updatedCommentsDataMap.inline[threadKey].location as CommentInlineLocation)
										.inlineResolveProperties,
									isDangling: false,
								} as ActiveCommentInlineResolveProperties,
							},
						},
					},
				});

				commentDanglingRef.current = null;
			}
		},
		onError: () => {
			// TODO: handle error, for now it's okay if not since BE will eventually handle the dangling comment
			// we could retry this mutation if we want to
		},
	});

	return { markCommentAsDangling, unmarkCommentAsDangling, commentDanglingRef };
};
